import React, { ComponentProps, forwardRef } from "react";

import classNames from "@/helpers/classNames";
import { LinkWithSlug } from "@/routes";
import { Menu } from "@headlessui/react";

// Polymorphic typing for button or anchor
type ButtonProps = {
  as?: "button";
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
} & Omit<ComponentProps<"button">, "onClick">;

type AnchorProps = {
  as: "a";
} & Omit<ComponentProps<typeof LinkWithSlug>, "children">;

type SharedProps = {
  text: string;
  disabled?: boolean;
  size?: "sm" | "lg";
  danger?: boolean;
  tooltipText?: string;
  icon?: React.ReactNode;
};

type Props = SharedProps & (ButtonProps | AnchorProps);

const MenuItem = forwardRef<HTMLButtonElement | HTMLAnchorElement, Props>(
  (props, ref) => {
    const {
      text,
      size,
      disabled,
      danger,
      tooltipText,
      icon,
      as = "button",
      ...rest
    } = props;

    const renderContent = () => (
      <>
        {icon && (
          <span className="mr-2 flex flex-shrink-0 items-center justify-center">
            {icon}
          </span>
        )}
        <span className="flex-grow">{text}</span>
        {tooltipText && (
          <span className="flex-shrink-0 text-2xs text-muted-foreground">
            {tooltipText}
          </span>
        )}
      </>
    );

    return (
      <Menu.Item ref={ref as any}>
        {({ active }) => {
          const className = classNames(
            active ? "bg-gray-100 dark:bg-gray-700" : "",
            size === "sm" ? "px-3 py-2 text-xs" : "px-4 py-2 text-sm",
            disabled ? "cursor-default opacity-50" : "text-gray-900",
            danger ? "text-red-500" : "",
            "flex w-full text-left dark:border-gray-600 dark:text-white",
            props.className,
          );

          if (as === "a") {
            const { to, onClick, ...anchorRest } = rest as AnchorProps;
            return (
              <LinkWithSlug
                to={disabled ? undefined : to}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  if (disabled) {
                    e.preventDefault();
                    return;
                  }
                  e.stopPropagation();
                  onClick?.(e);
                }}
                {...anchorRest}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                className={className}
              >
                {renderContent()}
              </LinkWithSlug>
            );
          }

          // Default: button
          const { onClick, ...buttonRest } = rest as ButtonProps;
          return (
            <button
              ref={ref as React.Ref<HTMLButtonElement>}
              type="button"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                if (disabled) return;
                e.stopPropagation();
                onClick(e);
              }}
              disabled={disabled}
              {...buttonRest}
              className={className}
            >
              {renderContent()}
            </button>
          );
        }}
      </Menu.Item>
    );
  },
);

export default MenuItem;
